/**
 * Typed tRPC client factory for the web workspace.
 *
 * Single composition point that builds a `CreateTRPCClient<AppRouter>`
 * over the standard `httpBatchLink`. Adapter classes (`TrpcContentReader`
 * and the future Cluster 3/4 ports) consume an instance of `WebTrpcClient`
 * via constructor injection so unit tests can substitute a hand-rolled
 * mock without coupling to the network layer.
 *
 * Why a factory and not a module-scope const
 * ==========================================
 *
 * Module-scope construction would create the client at import time,
 * which is incompatible with two requirements:
 *
 *   1. Test isolation — a per-test mock client would be impossible if
 *      the singleton was already wired into module-scope dependents.
 *   2. SSR / future deferred init — composition root constructs the
 *      client once at render time, alongside the other port factories
 *      (`useSessionPort`, `useLanguagePort`, `useContentPort`).
 *
 * Both production and tests import the factory, never a global.
 *
 * URL convention
 * ==============
 *
 * The default `'/trpc'` matches the Vite dev-server proxy
 * (`apps/web/vite.config.ts`) and the production deploy assumption that
 * the API is reachable at the same origin under `/trpc`. Callers may
 * override the URL when wiring against a different origin (e.g. an
 * integration test pointing at a Testcontainers instance).
 *
 * Authentication
 * ==============
 *
 * The client sends no `Authorization` header. Per D-089 the API derives
 * its request context from server-side facts, and the BFF session
 * cookie (`__Secure-oma_session`, `HttpOnly`) rides every same-origin
 * request automatically, so no credential is held or attached in JS.
 */

import type { AppRouter } from '@omaplus/shared-types/trpc';
import type { CreateTRPCClient } from '@trpc/client';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { MEMBER_SESSION_EXPIRED_EVENT } from '../session/session-expiry.js';
import { invalidateSession } from '../session/sessionInvalidation.js';

/**
 * The fully-typed tRPC client surface used by web-side adapters. The
 * type alias is exported so adapters can declare their dependency
 * without re-deriving the generic at every call site.
 */
export type WebTrpcClient = CreateTRPCClient<AppRouter>;

/**
 * The fetch shape `httpBatchLink` accepts via its `fetch` option. Mirrors
 * `@trpc/client`'s internal `FetchEsque` (which is intentionally not
 * re-exported), narrowed here to the shape the link actually invokes:
 * `(url, init) => Promise<Response-like>`. Production callers leave
 * `fetch` undefined so the link uses the global `fetch`; the test seam
 * passes a mock that captures the outgoing `RequestInit`.
 *
 * tRPC's `FetchEsque` is a deliberately looser superset
 * (`RequestInit | RequestInitEsque` init, `ResponseEsque` result) and is
 * not re-exported, so neither this hand-narrowed mirror nor `typeof fetch`
 * is structurally assignable to it under `strictFunctionTypes`
 * (contravariant `init` mismatch). The single boundary cast in
 * {@link createWebTrpcClient} bridges that gap explicitly — see there.
 */
type WebTrpcFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Options accepted by {@link createWebTrpcClient}. Kept as an
 * options-object so future additions (per-request headers, abort
 * signals, custom transformers) are additive rather than breaking.
 */
export interface CreateWebTrpcClientOptions {
  /**
   * Base URL the client should target. Defaults to `'/trpc'`, the
   * same-origin path the Vite dev-server proxy and the production
   * deploy convention both expose.
   */
  readonly url?: string;
  /**
   * @internal
   *
   * Test-seam-only override for the `fetch` implementation that
   * `httpBatchLink` uses to drive HTTP. Production callers must leave
   * this undefined so the link uses the global `fetch`; tests pass a
   * mock that captures the outgoing request and returns a synthesised
   * tRPC response. Surfaced on the options interface so the test file
   * does not have to reach into implementation internals; the
   * `@internal` tag flags it for tooling/readers as not part of the
   * public surface.
   */
  // `?: T | undefined` under `exactOptionalPropertyTypes`: production
  // callers leave this `undefined` (documented above); the call site
  // branches on it so an explicit `undefined` never reaches tRPC's
  // `fetch?: FetchEsque`.
  readonly fetch?: WebTrpcFetch | undefined;
}

/**
 * Construct a typed tRPC client targeting the API's `AppRouter`.
 *
 * Composition root (`main.tsx` via `useContentPort`) calls this once
 * per React root; the resulting client is passed to adapter
 * constructors. Tests construct a hand-rolled mock that satisfies the
 * subset of {@link WebTrpcClient} the adapter actually uses; no real
 * client is ever wired in unit tests.
 */
export function createWebTrpcClient(options: CreateWebTrpcClientOptions = {}): WebTrpcClient {
  const url = options.url ?? '/trpc';
  const { fetch: fetchOverride } = options;
  // The single boundary cast below is deliberate and tightly scoped:
  // tRPC's `fetch?: FetchEsque` type is intentionally NOT re-exported, and
  // `FetchEsque` is a looser superset of the runtime fetch contract
  // (`RequestInit | RequestInitEsque` init, `ResponseEsque` result). Under
  // `strictFunctionTypes` no honestly-authored local fetch type — neither
  // this `WebTrpcFetch` mirror nor `typeof fetch` — is structurally
  // assignable to it (contravariant `init` mismatch), yet the runtime
  // contract is sound (`httpBatchLink` only ever calls `fetch(url, init)`
  // and reads a `Response`). The cast bridges exactly that
  // unexpressible-without-the-private-type gap at one line, with no `any`,
  // no `@ts-expect-error`, and no flag loosening.
  const linkOptions: Parameters<typeof httpBatchLink>[0] = { url };
  const fetchWithExpiry: WebTrpcFetch = async (input, init) => {
    const response = fetchOverride
      ? await fetchOverride(input, init)
      : await globalThis.fetch(input, init);
    // Mixed tRPC batches use 207; a single refused operation uses 401. Inspect
    // a clone so the ordinary link still owns decoding and propagating errors.
    if (response.status === 401 || response.status === 207) {
      let messages: unknown[] = [];
      try {
        messages = unauthorizedErrorMessages(await response.clone().json());
      } catch {
        /* The regular tRPC decoder owns malformed responses. */
      }
      if (response.status === 401 || messages.length > 0) invalidateSession();
      if (messages.includes('member-session-expired') && typeof window !== 'undefined') {
        window.dispatchEvent(new Event(MEMBER_SESSION_EXPIRED_EVENT));
      }
    }
    return response;
  };
  linkOptions.fetch = fetchWithExpiry as NonNullable<typeof linkOptions.fetch>;
  return createTRPCClient<AppRouter>({ links: [httpBatchLink(linkOptions)] });
}

/** Messages of the UNAUTHORIZED errors in a single or batched tRPC response body. */
function unauthorizedErrorMessages(body: unknown): unknown[] {
  const entries: unknown[] = Array.isArray(body) ? body : [body];
  return entries.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null || !('error' in entry)) return [];
    const error = entry.error;
    if (typeof error !== 'object' || error === null || !('data' in error)) return [];
    const data = error.data;
    if (typeof data !== 'object' || data === null || !('code' in data)) return [];
    if (data.code !== 'UNAUTHORIZED') return [];
    return ['message' in error ? error.message : undefined];
  });
}
